// Root route — the app shell. Mounts the SAME provider stack + chrome as
// apps/web's app/layout.tsx (ClerkProvider > ThemeProvider > TooltipProvider
// > ClientLayout), swapping only what index.html already owns (no <html>/
// <body>/fonts here — see index.html) and next/font for @fontsource
// (TODO(G1): wire @fontsource-variable/manrope + space-grotesk per
// FRONTEND_SPA_DESIGN.md §5c; index.css currently inherits apps/web's
// globals.css @font-face-free Google Fonts @import for Playfair only, so
// Manrope/Space Grotesk are unstyled until that lands).
//
// ClientLayout (`@/components/client-layout`) is reused WHOLESALE, not
// reimplemented — it already contains the sidebar mount, mobile sheet,
// onboarding modal, and the pathname-based chromeless detection for
// /sign-in, /v/:id, /s/:id, /d/:id, /screenshot/:id (client-layout.tsx:38-39).
// This is the design doc's explicit "cheapest first cut" (§3a): keeping
// ClientLayout's pathname-sniffing (now backed by our next/navigation ->
// TanStack shim) instead of a parallel `_chrome`/`_bare` route split.
import { Suspense } from 'react';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { ClerkProvider } from '@clerk/clerk-react';
import { ClientLayout } from '@/components/client-layout';
import { ThemeProvider } from '@/components/theme-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { env } from '../env';
import type { RouterContext } from '../lib/auth';

function RootComponent() {
  return (
    <ClerkProvider publishableKey={env.clerkPublishableKey}>
      {/* Skip-to-main: WCAG 2.4.1 bypass, ported verbatim from app/layout.tsx:83-88. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:rounded-lg focus:bg-violet-600 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-[0_8px_24px_rgba(124,58,237,0.5)] focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 focus:ring-offset-background"
      >
        Skip to main content
      </a>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
        <TooltipProvider>
          <ClientLayout>
            <div id="main" tabIndex={-1} className="outline-none h-full">
              {/* Suspense boundary for placeholder routes lazy-loaded via
                  React.lazy (see routes/placeholder-page.tsx + THE
                  CONVENTIONS in apps/spa's handoff notes) and for any
                  reused apps/web component still wrapped in its own
                  `<Suspense>` (e.g. app/page.tsx's GenUIChat wrapper). */}
              <Suspense fallback={null}>
                <Outlet />
              </Suspense>
            </div>
          </ClientLayout>
        </TooltipProvider>
      </ThemeProvider>
    </ClerkProvider>
  );
}

function NotFoundComponent() {
  return (
    <div className="flex h-full items-center justify-center p-8 text-center">
      <div>
        <p className="text-lg font-semibold">Page not found</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {typeof window !== 'undefined' ? window.location.pathname : ''}
        </p>
      </div>
    </div>
  );
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});
